'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface EasterEggsProps {
  konamiActivated: boolean
  onKonamiClose: () => void
}

const characters = [
  { name: 'Aurora', emoji: '🍍', color: '#f5d942', title: 'A Milagrosa', location: 'Milagrosa' },
  { name: 'Lorena', emoji: '🥑', color: '#6ec665', title: 'A Zen', location: 'S. Bento' },
  { name: 'Stefani', emoji: '🍉', color: '#ff6b6b', title: 'A Solar', location: 'S. José' },
  { name: 'Evy', emoji: '🍒', color: '#e84393', title: 'A Noturna', location: 'Sta. Teresinha' },
  { name: 'Angel', emoji: '🍇', color: '#a55eea', title: 'O Protetor', location: 'Padre Pio' },
]

export function EasterEggs({ konamiActivated, onKonamiClose }: EasterEggsProps) {
  const [showKonami, setShowKonami] = useState(false)
  const [shakeCount, setShakeCount] = useState(0)
  const lastShakeRef = useRef(0)

  useEffect(() => {
    if (konamiActivated && !showKonami) {
      setShowKonami(true)
    }
  }, [konamiActivated, showKonami])

  const handleShake = () => {
    const now = Date.now()
    if (now - lastShakeRef.current > 1000) {
      setShakeCount(0)
    }
    setShakeCount(c => c + 1)
    lastShakeRef.current = now

    if (shakeCount >= 3) {
      setShowKonami(true)
      setShakeCount(0)
    }
  }

  useEffect(() => {
    const handleDeviceMotion = (e: DeviceMotionEvent) => {
      const acc = e.accelerationIncludingGravity
      if (acc) {
        const x = acc.x ?? 0
        const y = acc.y ?? 0
        const z = acc.z ?? 0
        const magnitude = Math.sqrt(x * x + y * y + z * z)
        if (magnitude > 25) handleShake()
      }
    }
    window.addEventListener('devicemotion', handleDeviceMotion)
    return () => window.removeEventListener('devicemotion', handleDeviceMotion)
  }, [shakeCount])

  if (!showKonami) return null

  return (
    <AnimatePresence onExitComplete={onKonamiClose}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onKonamiClose}
        role="dialog"
        aria-modal="true"
        aria-label="Konami Code Activated - Squibbles Secret"
      >
        <motion.div
          initial={{ scale: 0.8, y: 50, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.8, y: -50, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.68, -0.55, 0.265, 1.55] }}
          className="relative w-full max-w-md glass-strong rounded-3xl p-8 overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onKonamiClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
            aria-label="Fechar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </motion.button>

          <div className="text-center">
            <motion.div
              animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-block mb-6"
            >
              <span className="text-6xl" role="img" aria-label="Squibbles">✨</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-pink-400 to-purple-400 bg-clip-text text-transparent mb-2"
            >
              CÓDIGO KONAMI ATIVADO!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-zinc-400 mb-8 text-sm"
            >
              Você encontrou o easter egg secreto dos Squibbles! Conheça a turma:
            </motion.p>

            <AnimatePresence>
              <motion.div
                key="characters"
                layout
                className="space-y-3 max-h-64 overflow-y-auto pr-2"
              >
                {characters.map((char, i) => (
                  <motion.div
                    key={char.name}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ delay: 0.4 + i * 0.08, ease: [0.34, 1.56, 0.64, 1] }}
                    className="flex items-center gap-4 p-3 rounded-xl glass group"
                    style={{ borderLeft: `4px solid ${char.color}` }}
                  >
                    <motion.span
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                      className="text-3xl"
                      role="img"
                      aria-label={char.emoji}
                    >
                      {char.emoji}
                    </motion.span>
                    <div className="flex-1 text-left min-w-0">
                      <motion.p className="font-semibold text-white text-sm">{char.name}</motion.p>
                      <motion.p className="text-zinc-500 text-xs font-mono">{char.title} • {char.location}</motion.p>
                    </div>
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold text-black"
                      style={{ background: char.color }}
                    >
                      {char.emoji}
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-6 text-zinc-500 text-xs font-mono"
            >
              ↑↑↓↓←→←→BA + Enter
            </motion.p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}